import { useMemo } from "react";
import { Gift, Printer, X } from "lucide-react";

import { Money } from "@workspace/accounting/components";
import { Badge } from "@workspace/ui/components/badge";
import { Button } from "@workspace/ui/components/button";
import { cn } from "@workspace/ui/lib/utils";

import type { EposCourse, OrderItem } from "../../types";
import { COMPD_LABEL } from "../comp";
import type { LineSyncStatus } from "../epos-context/order-write-queue";
import { LineSyncBadge } from "./line-sync-badge";
import { OptionsSummary } from "./options-summary";
import { prepTimeLabel } from "./prep-time";

interface CourseGroup {
	course: EposCourse | null;
	items: OrderItem[];
	status: "held" | "sent" | "partial";
}

interface CourseGroupedOrderItemsProps {
	items: OrderItem[];
	courses: EposCourse[];
	firedCourseIds?: string[];
	pendingCourseId?: string | null;
	isEditable: boolean;
	isSplit: boolean;
	onEditItem: (item: OrderItem) => void;
	onRemoveItem: (itemId: string) => void;
	onResend?: (courseId: string) => void;
	/** HA-768 / D-HA-24 — each line's unconfirmed-write status, and the Retry for a failed one. */
	lineStatus?: (itemId: string) => LineSyncStatus | null;
	onRetryLine?: () => void;
	/**
	 * HA-658 slice 4 — the lines the order's comps cover (each reads Comp'd (£0) and can't be edited until its comp is removed, R2),
	 * and the Comp / Remove Comp controls, offered only to a till user holding the comp operation while the order can take one.
	 */
	compedItemIds?: ReadonlySet<string>;
	compReasons?: ReadonlyMap<string, string>;
	onComp?: (item: OrderItem) => void;
	onRemoveComp?: (itemId: string) => void;
}

export function CourseGroupedOrderItems({
	items,
	courses,
	firedCourseIds = [],
	pendingCourseId,
	isEditable,
	isSplit,
	onEditItem,
	onRemoveItem,
	onResend,
	lineStatus,
	onRetryLine,
	compedItemIds,
	compReasons,
	onComp,
	onRemoveComp,
}: CourseGroupedOrderItemsProps) {
	const groups = useMemo(() => {
		const standardCourses = courses
			.filter((c) => !c.autoFire)
			.sort((a, b) => a.sortOrder - b.sortOrder);
		const autoFireCourses = courses
			.filter((c) => c.autoFire)
			.sort((a, b) => a.sortOrder - b.sortOrder);

		const result: CourseGroup[] = [];

		// Standard courses first, then auto-fire, in sort order
		for (const course of [...standardCourses, ...autoFireCourses]) {
			const courseItems = items.filter((i) => i.courseId === course.id);
			if (courseItems.length === 0) continue;

			const allSent = courseItems.every((i) => i.sentToKitchen);
			const noneSent = courseItems.every((i) => !i.sentToKitchen);

			result.push({
				course,
				items: courseItems,
				status: allSent ? "sent" : noneSent ? "held" : "partial",
			});
		}

		// Ungrouped items (no courseId)
		const ungrouped = items.filter((i) => !i.courseId);
		if (ungrouped.length > 0) {
			const allSent = ungrouped.every((i) => i.sentToKitchen);
			const noneSent = ungrouped.every((i) => !i.sentToKitchen);
			result.push({
				course: null,
				items: ungrouped,
				status: allSent ? "sent" : noneSent ? "held" : "partial",
			});
		}

		return result;
	}, [items, courses]);

	return (
		<div>
			{groups.map((group) => (
				<div key={group.course?.id ?? "ungrouped"}>
					<CourseHeader
						course={group.course}
						status={group.status}
						isFired={
							group.course ? firedCourseIds.includes(group.course.id) : false
						}
						isPending={group.course?.id === pendingCourseId}
						onResend={
							onResend &&
							group.course &&
							firedCourseIds.includes(group.course.id)
								? () => onResend(group.course!.id)
								: undefined
						}
					/>
					{group.items.map((item) => (
						<OrderItemRow
							key={item.id}
							item={item}
							isEditable={isEditable}
							isSplit={isSplit}
							onEdit={onEditItem}
							onRemove={onRemoveItem}
							syncStatus={lineStatus?.(item.id) ?? null}
							onRetry={onRetryLine}
							comped={compedItemIds?.has(item.id) ?? false}
							compReason={compReasons?.get(item.id)}
							onComp={onComp}
							onRemoveComp={onRemoveComp}
						/>
					))}
				</div>
			))}
		</div>
	);
}

function CourseHeader({
	course,
	status,
	isFired,
	isPending,
	onResend,
}: {
	course: EposCourse | null;
	status: "held" | "sent" | "partial";
	isFired: boolean;
	isPending: boolean;
	onResend?: () => void;
}) {
	const label = course?.name ?? "Ungrouped";
	const isAutoFire = course?.autoFire ?? false;

	const statusLabel = isPending
		? "Firing..."
		: isFired || status === "sent"
			? "Sent"
			: isAutoFire
				? "Unsent"
				: status === "partial"
					? "Partial"
					: "Held";

	const statusVariant = isPending
		? "secondary"
		: isFired || status === "sent"
			? "default"
			: status === "partial"
				? "secondary"
				: "outline";

	return (
		<div className="flex items-center justify-between px-2 py-1.5 bg-muted/30 border-b">
			<span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
				{label}
				{isAutoFire && (
					<span className="ml-1 text-[10px] font-normal">(auto-fire)</span>
				)}
			</span>
			<div className="flex items-center gap-1">
				<Badge variant={statusVariant} className="text-[10px] px-1.5 py-0">
					{statusLabel}
				</Badge>
				{onResend && (
					<Button
						variant="outline"
						size="sm"
						className="h-6 text-[10px] px-2"
						onClick={onResend}
					>
						<Printer className="size-3" />
						Resend
					</Button>
				)}
			</div>
		</div>
	);
}

function OrderItemRow({
	item,
	isEditable,
	isSplit,
	onEdit,
	onRemove,
	syncStatus,
	onRetry,
	comped,
	compReason,
	onComp,
	onRemoveComp,
}: {
	item: OrderItem;
	isEditable: boolean;
	isSplit: boolean;
	onEdit: (item: OrderItem) => void;
	onRemove: (itemId: string) => void;
	syncStatus: LineSyncStatus | null;
	onRetry?: () => void;
	comped: boolean;
	compReason?: string;
	onComp?: (item: OrderItem) => void;
	onRemoveComp?: (itemId: string) => void;
}) {
	const hasOptions = item.selectedOptions && item.selectedOptions.length > 0;

	return (
		<div
			className={cn(
				"flex items-center justify-between border-b hover:bg-muted/50 transition-colors",
				{
					"items-start": item.notes || hasOptions,
				},
			)}
		>
			<button
				type="button"
				className="flex-1 items-start h-full text-left cursor-pointer py-4 pl-1"
				onClick={() => onEdit(item)}
				// A comped line is locked until its comp is removed (HA-658 R2).
				disabled={isSplit || !isEditable || comped}
			>
				<div className="flex items-center justify-between">
					<div className="flex items-center gap-1.5 font-medium max-w-[13.5rem] truncate">
						{item.sentToKitchen && (
							<Badge
								variant="secondary"
								className="text-[10px] px-1 py-0 shrink-0"
							>
								Sent
							</Badge>
						)}
						{item.name}
					</div>
					<span>
						{comped ? (
							<Badge variant="outline">{COMPD_LABEL}</Badge>
						) : (
							<Money {...item.price} />
						)}
					</span>
				</div>
				{comped && compReason && (
					<div className="mt-1 text-xs text-muted-foreground">
						Reason: {compReason}
					</div>
				)}
				{prepTimeLabel(item) && (
					<div className="mt-1 text-xs text-muted-foreground">
						{prepTimeLabel(item)}
					</div>
				)}
				{hasOptions && (
					<div className="mt-1 text-sm text-muted-foreground">
						<OptionsSummary
							selectedOptions={item.selectedOptions}
							removedIngredients={item.removedIngredients}
							className="text-xs"
						/>
					</div>
				)}
				{item.notes && (
					<Badge
						variant="outline"
						className="border-muted text-muted-foreground"
					>
						{item.notes}
					</Badge>
				)}
			</button>
			<LineSyncBadge status={syncStatus} onRetry={onRetry} />
			{!isSplit && comped && onRemoveComp && (
				<Button
					variant="outline"
					size="lg"
					className="mx-1 my-2 h-11"
					onClick={() => onRemoveComp(item.id)}
					aria-label={`Remove Comp on ${item.name}`}
				>
					Remove Comp
				</Button>
			)}
			{!isSplit && !comped && onComp && (
				<Button
					variant="outline"
					size="icon-xl"
					className="mx-1 my-2"
					onClick={() => onComp(item)}
					aria-label={`Comp ${item.name}`}
				>
					<Gift className="size-6" />
				</Button>
			)}
			{!isSplit && isEditable && (
				<div className="flex gap-1 mx-1 my-2">
					<Button
						variant="outline"
						size="icon-xl"
						onClick={() => onRemove(item.id)}
						disabled={syncStatus === "removing"}
						aria-label="Remove item"
					>
						<X className="size-6" />
					</Button>
				</div>
			)}
		</div>
	);
}
